<script setup>
import { computed, onMounted, reactive, ref } from "vue"
import { useRouter } from "vue-router"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import { useAuth } from "@/composables/useAuth"

const router = useRouter()
const { hasRole } = useAuth()

// Solo admin/rrhh pueden crear/editar/retirar empleados y gestionar
// documentos; el resto solo puede ver (y solo se ve a sí mismo, filtrado
// desde el backend en GET /rrhh/empleados).
const puedeGestionar = computed(() => hasRole("admin") || hasRole("rrhh"))

// admin/rrhh/supervisor pueden subir incapacidades a nombre de cualquier
// empleado (POST /rrhh/ausencias con empleadoId).
const puedeSubirIncapacidad = computed(() => puedeGestionar.value || hasRole("supervisor"))

// Si no gestiona, GET /rrhh/empleados solo devuelve su propia ficha (o
// ninguna si aún no la creó); en ese caso puede autoservirse una vez.
const puedeCrear = computed(() => puedeGestionar.value || empleados.value.length === 0)

const empleados = ref([])
const cargando = ref(true)

// El jefe inmediato de cualquier empleado solo puede ser alguien con rol
// RRHH (el backend valida lo mismo en POST/PUT /rrhh/empleados).
const jefesDisponibles = computed(() => empleados.value.filter((e) => e.rol === "rrhh"))

const filtros = reactive({
  q: "",
  estado: "activo"
})

// ─── Catálogos ────────────────────────────────────────────────────────────
const cargos = ref([])
const areas = ref([])
const tiposContrato = ref([])
const epsList = ref([])
const fondosPension = ref([])
const arlList = ref([])
const usuarios = ref([])

// EPS/fondo de pensión/ARL son datos personales: los necesita cualquiera
// que pueda crear su propia ficha. Cargo/área/tipo de contrato/usuarios son
// campos administrativos, solo para quien gestiona (admin/rrhh).
async function cargarCatalogosPersonales() {
  try {
    const [rEps, rFondoPension, rArl] = await Promise.all([
      api.get("/catalogos/eps"),
      api.get("/catalogos/fondoPension"),
      api.get("/catalogos/arl")
    ])
    epsList.value = rEps.data.items
    fondosPension.value = rFondoPension.data.items
    arlList.value = rArl.data.items
  } catch (err) {
    console.error(err)
  }
}

async function cargarCatalogosGestion() {
  try {
    const [rCargo, rArea, rTipoContrato, rUsuarios] = await Promise.all([
      api.get("/catalogos/cargo"),
      api.get("/catalogos/area"),
      api.get("/catalogos/tipoContrato"),
      api.get("/rrhh/usuarios")
    ])
    cargos.value = rCargo.data.items
    areas.value = rArea.data.items
    tiposContrato.value = rTipoContrato.data.items
    usuarios.value = rUsuarios.data.usuarios
  } catch (err) {
    console.error(err)
  }
}

async function cargarEmpleados() {
  cargando.value = true
  try {
    const params = { estado: filtros.estado }
    if (filtros.q) params.q = filtros.q

    const { data } = await api.get("/rrhh/empleados", { params })
    empleados.value = data.empleados
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el listado de empleados",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

onMounted(() => {
  cargarCatalogosPersonales()
  if (puedeGestionar.value) cargarCatalogosGestion()
  cargarEmpleados()
})

function formatDate(value) {
  if (!value) return ""
  return new Date(value).toLocaleDateString("es-CO", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" })
}

// ─── Modal crear/editar ──────────────────────────────────────────────────
const modalVisible = ref(false)
const guardando = ref(false)
const editandoId = ref(null)

const formVacio = () => ({
  userId: "",
  nombres: "",
  apellidos: "",
  cedula: "",
  fechaNacimiento: "",
  telefono: "",
  direccion: "",
  emailPersonal: "",
  cargoId: "",
  areaId: "",
  managerId: "",
  fechaIngreso: "",
  tipoContratoId: "",
  diasVacacionesAnuales: 15,
  vacacionesAdelantadas: 0,
  epsId: "",
  fondoPensionId: "",
  arlId: "",
  contactoEmergenciaNombre: "",
  contactoEmergenciaTelefono: "",
  contactoEmergenciaParentesco: "",
  alergias: ""
})

const form = reactive(formVacio())

// La cédula llega enmascarada por defecto (últimos 4 dígitos) desde
// GET /rrhh/empleados; el número completo solo se pide bajo demanda y
// queda auditado en el backend (registrarEventoSeguridad).
const cedulasReveladas = ref({})

async function revelarCedula(empleado) {
  try {
    const { data } = await api.get(`/rrhh/empleados/${empleado.id}/cedula`)
    cedulasReveladas.value[empleado.id] = data.cedula
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo ver la cédula completa",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

function abrirNuevo() {
  editandoId.value = null
  Object.assign(form, formVacio())
  modalVisible.value = true
}

async function abrirEditar(empleado) {
  editandoId.value = empleado.id

  // El listado solo trae la cédula enmascarada: para no pisarla con la
  // versión enmascarada al guardar, se pide el valor completo antes de
  // abrir el formulario (esta consulta también queda auditada).
  let cedulaCompleta = empleado.cedula || ""
  try {
    const { data } = await api.get(`/rrhh/empleados/${empleado.id}/cedula`)
    cedulaCompleta = data.cedula || ""
  } catch (err) {
    console.error(err)
  }

  Object.assign(form, {
    userId: empleado.userId || "",
    nombres: empleado.nombres,
    apellidos: empleado.apellidos,
    cedula: cedulaCompleta,
    fechaNacimiento: empleado.fechaNacimiento?.slice(0, 10) || "",
    telefono: empleado.telefono || "",
    direccion: empleado.direccion || "",
    emailPersonal: empleado.emailPersonal || "",
    cargoId: empleado.cargoId || "",
    areaId: empleado.areaId || "",
    managerId: empleado.managerId || "",
    fechaIngreso: empleado.fechaIngreso?.slice(0, 10) || "",
    tipoContratoId: empleado.tipoContratoId || "",
    diasVacacionesAnuales: Number(empleado.diasVacacionesAnuales) || 15,
    vacacionesAdelantadas: Number(empleado.vacacionesAdelantadas) || 0,
    epsId: empleado.epsId || "",
    fondoPensionId: empleado.fondoPensionId || "",
    arlId: empleado.arlId || "",
    contactoEmergenciaNombre: empleado.contactoEmergenciaNombre || "",
    contactoEmergenciaTelefono: empleado.contactoEmergenciaTelefono || "",
    contactoEmergenciaParentesco: empleado.contactoEmergenciaParentesco || "",
    alergias: empleado.alergias || ""
  })
  modalVisible.value = true
}

function cerrarModal() {
  modalVisible.value = false
}

async function guardar() {
  if (!form.nombres || !form.apellidos || !form.fechaIngreso) {
    Swal.fire({ icon: "warning", title: "Faltan datos", text: "Nombres, apellidos y fecha de ingreso son obligatorios." })
    return
  }

  guardando.value = true
  const esNuevo = !editandoId.value
  try {
    const payload = {
      ...form,
      userId: form.userId || null,
      managerId: form.managerId || null,
      cargoId: form.cargoId || null,
      areaId: form.areaId || null,
      tipoContratoId: form.tipoContratoId || null,
      epsId: form.epsId || null,
      fondoPensionId: form.fondoPensionId || null,
      arlId: form.arlId || null
    }

    let nuevoId = editandoId.value
    if (editandoId.value) {
      await api.put(`/rrhh/empleados/${editandoId.value}`, payload)
    } else {
      const { data } = await api.post("/rrhh/empleados", payload)
      nuevoId = data.id
    }

    modalVisible.value = false
    await cargarEmpleados()

    if (esNuevo && puedeGestionar.value) {
      await Swal.fire({
        icon: "success",
        title: "Empleado creado",
        text: "Ahora carga su hoja de vida, cédula y contrato en el expediente.",
        confirmButtonText: "Ir al expediente"
      })
      router.push({ name: "RRHHExpediente", params: { id: nuevoId } })
    } else if (esNuevo) {
      Swal.fire({
        icon: "success",
        title: "Ficha creada",
        text: "RRHH completará tu cargo, área y contrato próximamente."
      })
    } else {
      Swal.fire({ icon: "success", title: "Empleado actualizado", timer: 1600, showConfirmButton: false })
    }
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}

async function retirar(empleado) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Marcar como retirado?",
    text: `Se marcará a "${empleado.nombres} ${empleado.apellidos}" como retirado. El expediente se conserva.`,
    showCancelButton: true,
    confirmButtonText: "Sí, retirar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  })
  if (!isConfirmed) return

  try {
    await api.delete(`/rrhh/empleados/${empleado.id}`)
    await cargarEmpleados()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo retirar al empleado",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

function irAExpediente(empleado) {
  router.push({ name: "RRHHExpediente", params: { id: empleado.id } })
}

// ─── Detalle de ausencias ─────────────────────────────────────────────────
const LABELS_TIPO_AUSENCIA = {
  permiso: "Permiso",
  incapacidad: "Incapacidad",
  cita_medica: "Cita médica",
  otro: "Otro"
}

async function verAusencias(empleado) {
  try {
    const { data } = await api.get(`/rrhh/empleados/${empleado.id}/ausencias-detalle`)
    const filas = data.ausencias.length
      ? data.ausencias.map((a) =>
          `<tr>
            <td>${LABELS_TIPO_AUSENCIA[a.tipo] || a.tipo}</td>
            <td>${formatDate(a.fechaInicio)} - ${formatDate(a.fechaFin)}</td>
            <td>${a.diasSolicitados}</td>
            <td>${a.motivo || "—"}</td>
          </tr>`
        ).join("")
      : `<tr><td colspan="4" class="text-center text-muted">Sin ausencias reportadas.</td></tr>`

    Swal.fire({
      title: `Ausencias de ${empleado.nombres} ${empleado.apellidos}`,
      html: `
        <div class="table-responsive">
          <table class="table table-sm">
            <thead><tr><th>Tipo</th><th>Fechas</th><th>Días</th><th>Motivo</th></tr></thead>
            <tbody>${filas}</tbody>
          </table>
        </div>
      `,
      width: 600
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el detalle de ausencias",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

// ─── Subir incapacidad a nombre de un empleado ────────────────────────────
const incapacidadEmpleado = ref(null)
const guardandoIncapacidad = ref(false)
const formIncapacidad = reactive({ fechaInicio: "", fechaFin: "", motivo: "", soporte: null })

function abrirIncapacidad(empleado) {
  Object.assign(formIncapacidad, { fechaInicio: "", fechaFin: "", motivo: "", soporte: null })
  incapacidadEmpleado.value = empleado
}

function cerrarIncapacidad() {
  incapacidadEmpleado.value = null
}

async function guardarIncapacidad() {
  if (!formIncapacidad.fechaInicio || !formIncapacidad.fechaFin) {
    Swal.fire({ icon: "warning", title: "Faltan datos", text: "Fecha de inicio y fecha de fin son obligatorias." })
    return
  }

  guardandoIncapacidad.value = true
  try {
    const formData = new FormData()
    formData.append("empleadoId", incapacidadEmpleado.value.id)
    formData.append("tipo", "incapacidad")
    formData.append("fechaInicio", formIncapacidad.fechaInicio)
    formData.append("fechaFin", formIncapacidad.fechaFin)
    if (formIncapacidad.motivo) formData.append("motivo", formIncapacidad.motivo)
    if (formIncapacidad.soporte) formData.append("soporte", formIncapacidad.soporte)

    await api.post("/rrhh/ausencias", formData, { headers: { "Content-Type": "multipart/form-data" } })

    const { nombres, apellidos } = incapacidadEmpleado.value
    incapacidadEmpleado.value = null
    Swal.fire({
      icon: "success",
      title: "Incapacidad registrada",
      text: `Se registró la incapacidad de ${nombres} ${apellidos}.`,
      timer: 2200,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo registrar la incapacidad",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardandoIncapacidad.value = false
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 class="mb-0">
          <i class="ni ni-single-02 text-primary me-2"></i>
          Empleados
        </h5>
        <button v-if="puedeCrear" type="button" class="btn btn-primary btn-sm mb-0" @click="abrirNuevo">
          <i class="ni ni-fat-add me-1"></i> {{ puedeGestionar ? "Nuevo empleado" : "Crear mi ficha" }}
        </button>
      </div>

      <div class="card-body">
        <div class="row g-2 mb-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label text-sm mb-1">Buscar (nombre / cédula)</label>
            <input type="text" class="form-control form-control-sm" v-model="filtros.q" placeholder="Ej: Pérez">
          </div>
          <div class="col-md-3">
            <label class="form-label text-sm mb-1">Estado</label>
            <select class="form-select form-select-sm" v-model="filtros.estado">
              <option value="activo">Activos</option>
              <option value="retirado">Retirados</option>
            </select>
          </div>
          <div class="col-md-3 d-flex gap-2">
            <button type="button" class="btn btn-outline-primary btn-sm mb-0 w-100" @click="cargarEmpleados">
              Filtrar
            </button>
          </div>
        </div>

        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando empleados...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped table-bordered align-middle table-sm">
            <thead class="table-dark">
              <tr>
                <th>Nombre</th>
                <th>Cédula</th>
                <th>Cargo</th>
                <th>Área</th>
                <th>Jefe inmediato</th>
                <th>Ingreso</th>
                <th>Estado</th>
                <th>Vacaciones</th>
                <th>Ausencias</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="empleados.length === 0">
                <td colspan="10" class="text-center text-muted py-4">
                  No hay empleados para los filtros seleccionados.
                </td>
              </tr>
              <tr v-for="e in empleados" :key="e.id">
                <td>{{ e.nombres }} {{ e.apellidos }}</td>
                <td>
                  <span>{{ cedulasReveladas[e.id] ?? e.cedula }}</span>
                  <button
                    v-if="e.cedula && cedulasReveladas[e.id] === undefined"
                    type="button"
                    class="btn btn-sm btn-link p-0 ms-1"
                    title="Ver cédula completa"
                    @click="revelarCedula(e)"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                </td>
                <td>{{ e.cargoNombre || e.cargo || "—" }}</td>
                <td>{{ e.areaNombre || e.area || "—" }}</td>
                <td>{{ e.jefeNombres ? `${e.jefeNombres} ${e.jefeApellidos}` : "—" }}</td>
                <td>{{ formatDate(e.fechaIngreso) }}</td>
                <td>
                  <span class="badge" :class="e.estado === 'activo' ? 'bg-success' : 'bg-secondary'">
                    {{ e.estado }}
                  </span>
                </td>
                <td>
                  <span v-if="e.saldoVacaciones" class="text-nowrap" :class="{ 'text-danger fw-bold': e.saldoVacaciones.saldo < 0 }">
                    {{ e.saldoVacaciones.saldo }} días
                  </span>
                  <span v-else>—</span>
                </td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-link p-0"
                    :disabled="!Number(e.diasAusenciaNoVacacional)"
                    @click="verAusencias(e)"
                  >
                    {{ e.diasAusenciaNoVacacional || 0 }} días
                  </button>
                </td>
                <td>
                  <div class="d-flex gap-1">
                    <button type="button" class="btn btn-sm btn-outline-dark" title="Expediente" @click="irAExpediente(e)">
                      <i class="bi bi-folder2-open"></i>
                    </button>
                    <button v-if="puedeGestionar" type="button" class="btn btn-sm btn-outline-primary" title="Editar" @click="abrirEditar(e)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button v-if="puedeSubirIncapacidad && e.estado === 'activo'" type="button" class="btn btn-sm btn-outline-warning" title="Subir incapacidad" @click="abrirIncapacidad(e)">
                      <i class="bi bi-bandaid"></i>
                    </button>
                    <button v-if="puedeGestionar && e.estado === 'activo'" type="button" class="btn btn-sm btn-outline-danger" title="Retirar" @click="retirar(e)">
                      <i class="bi bi-person-x"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR -->
    <div v-if="modalVisible" class="modal-backdrop-custom" @click.self="cerrarModal">
      <div class="modal-dialog-custom card shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">{{ editandoId ? "Editar empleado" : "Nuevo empleado" }}</h6>
          <button type="button" class="btn-close" @click="cerrarModal"></button>
        </div>
        <div class="card-body">
          <form class="row g-3" @submit.prevent="guardar">
            <div class="col-md-6">
              <label class="form-label">Nombres *</label>
              <input type="text" class="form-control" v-model="form.nombres" required>
            </div>
            <div class="col-md-6">
              <label class="form-label">Apellidos *</label>
              <input type="text" class="form-control" v-model="form.apellidos" required>
            </div>

            <div class="col-md-4">
              <label class="form-label">Cédula</label>
              <input type="text" class="form-control" v-model="form.cedula">
            </div>
            <div class="col-md-4">
              <label class="form-label">Fecha nacimiento</label>
              <input type="date" class="form-control" v-model="form.fechaNacimiento">
            </div>
            <div class="col-md-4">
              <label class="form-label">Teléfono</label>
              <input type="text" class="form-control" v-model="form.telefono">
            </div>

            <div class="col-md-6">
              <label class="form-label">Dirección</label>
              <input type="text" class="form-control" v-model="form.direccion">
            </div>
            <div class="col-md-6">
              <label class="form-label">Email personal</label>
              <input type="email" class="form-control" v-model="form.emailPersonal">
            </div>

            <template v-if="puedeGestionar">
              <div class="col-md-4">
                <label class="form-label">Cargo</label>
                <select class="form-select" v-model="form.cargoId">
                  <option value="">Selecciona...</option>
                  <option v-for="c in cargos" :key="c.id" :value="c.id">{{ c.nombre }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Área</label>
                <select class="form-select" v-model="form.areaId">
                  <option value="">Selecciona...</option>
                  <option v-for="a in areas" :key="a.id" :value="a.id">{{ a.nombre }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Jefe inmediato</label>
                <select class="form-select" v-model="form.managerId">
                  <option value="">Sin jefe asignado</option>
                  <option v-for="e in jefesDisponibles" :key="e.id" :value="e.id" :disabled="e.id === editandoId">
                    {{ e.nombres }} {{ e.apellidos }}
                  </option>
                </select>
              </div>
            </template>

            <div class="col-md-4">
              <label class="form-label">Fecha de ingreso *</label>
              <input type="date" class="form-control" v-model="form.fechaIngreso" required>
            </div>
            <template v-if="puedeGestionar">
              <div class="col-md-4">
                <label class="form-label">Tipo de contrato</label>
                <select class="form-select" v-model="form.tipoContratoId">
                  <option value="">Selecciona...</option>
                  <option v-for="tc in tiposContrato" :key="tc.id" :value="tc.id">{{ tc.nombre }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Días de vacaciones / año</label>
                <input type="number" class="form-control" v-model.number="form.diasVacacionesAnuales">
                <small class="text-muted">Referencial. Los días causados se calculan en tiempo real: 1,25 días por cada 30 días desde el ingreso (15/año).</small>
              </div>
              <div class="col-md-4">
                <label class="form-label">Vacaciones adelantadas (días)</label>
                <input type="number" step="0.5" class="form-control" v-model.number="form.vacacionesAdelantadas">
                <small class="text-muted">Días de vacaciones ya disfrutados antes de causarse. Se descuentan del saldo y pueden dejarlo en negativo.</small>
              </div>
            </template>

            <div class="col-md-4">
              <label class="form-label">EPS</label>
              <select class="form-select" v-model="form.epsId">
                <option value="">Selecciona...</option>
                <option v-for="item in epsList" :key="item.id" :value="item.id">{{ item.nombre }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Fondo de pensión</label>
              <select class="form-select" v-model="form.fondoPensionId">
                <option value="">Selecciona...</option>
                <option v-for="item in fondosPension" :key="item.id" :value="item.id">{{ item.nombre }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">ARL</label>
              <select class="form-select" v-model="form.arlId">
                <option value="">Selecciona...</option>
                <option v-for="item in arlList" :key="item.id" :value="item.id">{{ item.nombre }}</option>
              </select>
            </div>

            <div class="col-12">
              <hr class="my-2">
              <h6 class="text-sm text-uppercase text-muted">Contacto de emergencia</h6>
            </div>
            <div class="col-md-4">
              <label class="form-label">Nombre</label>
              <input type="text" class="form-control" v-model="form.contactoEmergenciaNombre">
            </div>
            <div class="col-md-4">
              <label class="form-label">Teléfono</label>
              <input type="text" class="form-control" v-model="form.contactoEmergenciaTelefono">
            </div>
            <div class="col-md-4">
              <label class="form-label">Parentesco</label>
              <input type="text" class="form-control" v-model="form.contactoEmergenciaParentesco">
            </div>

            <div class="col-12">
              <label class="form-label">Alergias</label>
              <textarea class="form-control" rows="2" v-model="form.alergias" placeholder="Ninguna conocida, si aplica"></textarea>
            </div>

            <div v-if="puedeGestionar" class="col-md-6">
              <label class="form-label">Usuario del sistema (opcional)</label>
              <select class="form-select" v-model="form.userId">
                <option value="">Sin vincular</option>
                <option v-for="usuario in usuarios" :key="usuario.id" :value="usuario.id">{{ usuario.name || usuario.email }}</option>
              </select>
              <small class="text-muted">Vincula esta ficha con una cuenta existente en Usuarios para habilitar el self-service.</small>
            </div>

            <div class="col-12 d-flex justify-content-end gap-2 mt-2">
              <button type="button" class="btn btn-outline-secondary mb-0" @click="cerrarModal">Cancelar</button>
              <button type="submit" class="btn btn-primary mb-0" :disabled="guardando">
                {{ guardando ? "Guardando..." : "Guardar" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- MODAL SUBIR INCAPACIDAD -->
    <div v-if="incapacidadEmpleado" class="modal-backdrop-custom" @click.self="cerrarIncapacidad">
      <div class="modal-dialog-custom card shadow-lg" style="max-width: 600px">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Subir incapacidad - {{ incapacidadEmpleado.nombres }} {{ incapacidadEmpleado.apellidos }}</h6>
          <button type="button" class="btn-close" @click="cerrarIncapacidad"></button>
        </div>
        <div class="card-body">
          <form class="row g-3" @submit.prevent="guardarIncapacidad">
            <div class="col-md-6">
              <label class="form-label">Fecha inicio *</label>
              <input type="date" class="form-control" v-model="formIncapacidad.fechaInicio" required>
            </div>
            <div class="col-md-6">
              <label class="form-label">Fecha fin *</label>
              <input type="date" class="form-control" v-model="formIncapacidad.fechaFin" required>
            </div>

            <div class="col-12">
              <label class="form-label">Motivo</label>
              <textarea class="form-control" rows="2" v-model="formIncapacidad.motivo"></textarea>
            </div>

            <div class="col-12">
              <label class="form-label">Soporte de la incapacidad</label>
              <input type="file" class="form-control" @change="formIncapacidad.soporte = $event.target.files[0] || null"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
            </div>

            <div class="col-12 d-flex justify-content-end gap-2 mt-2">
              <button type="button" class="btn btn-outline-secondary mb-0" @click="cerrarIncapacidad">Cancelar</button>
              <button type="submit" class="btn btn-primary mb-0" :disabled="guardandoIncapacidad">
                {{ guardandoIncapacidad ? "Guardando..." : "Registrar incapacidad" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-dialog-custom {
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
}
</style>
